/**
 * Vehicle Economy API service
 */

import api from '../api/client';
import type {
  VehicleEconomy,
  VehicleEconomyParty,
  VehicleEconomyPartyType,
  CostCenterOption,
  VehicleEconomyUpsertInput,
} from '@/types/vehicleEconomy';

const base = '/api/vehicles';

export interface VehicleEconomyHistoryRow {
  periodEndDate: string;
  agreementNo: string | null;
  contractNo: string | null;
  rentStart: string | null;
  rentEnd: string | null;
  termMonths: number | null;
  costMonthLeasing: number | null;
  leasingProduct: string | null;
  originalBasePrice: number | null;
  bookValue: number | null;
  guaranteedResidualValue: number | null;
  paymentAmount: number | null;
  periodNumber: number | null;
  invoiceReference: string | null;
}

export async function getEconomyHistory(vehicleId: number): Promise<VehicleEconomyHistoryRow[]> {
  const res = await api.get<{ success: boolean; data: VehicleEconomyHistoryRow[] }>(
    `${base}/${vehicleId}/economy/history`
  );
  return res.data.success ? res.data.data ?? [] : [];
}

export async function getEconomy(vehicleId: number): Promise<VehicleEconomy | null> {
  const res = await api.get<{ success: boolean; data: VehicleEconomy | null }>(
    `${base}/${vehicleId}/economy`
  );
  return res.data.success ? res.data.data : null;
}

function economyApiError(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const ax = err as { response?: { data?: { error?: string } }; message?: string };
    if (ax.response?.data?.error) return String(ax.response.data.error);
    if (ax.message) return ax.message;
  }
  return fallback;
}

export async function saveEconomy(
  vehicleId: number,
  data: VehicleEconomyUpsertInput
): Promise<VehicleEconomy> {
  try {
    const res = await api.put<{ success: boolean; data: VehicleEconomy; error?: string }>(
      `${base}/${vehicleId}/economy`,
      data
    );
    if (!res.data.success) throw new Error(res.data.error || 'Failed to save');
    return res.data.data;
  } catch (err) {
    throw new Error(economyApiError(err, 'Failed to save'));
  }
}

export async function getParties(
  organizationId?: string,
  type?: VehicleEconomyPartyType
): Promise<VehicleEconomyParty[]> {
  const params = new URLSearchParams();
  if (organizationId) params.set('organizationId', organizationId);
  if (type) params.set('type', type);
  const res = await api.get<{ success: boolean; data: VehicleEconomyParty[] }>(
    `${base}/economy/parties?${params.toString()}`
  );
  return res.data.success ? res.data.data : [];
}

export async function createParty(body: {
  organizationId?: string;
  type: VehicleEconomyPartyType;
  name: string;
  contact_person?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
}): Promise<{ id: string }> {
  try {
    const res = await api.post<{ success: boolean; data: { id: string }; error?: string }>(
      `${base}/economy/parties`,
      body
    );
    if (!res.data.success || !res.data.data?.id) {
      throw new Error(res.data.error || 'Failed to create party');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(economyApiError(err, 'Failed to create party'));
  }
}

export async function updateParty(
  id: string,
  body: Partial<{ name: string; type: VehicleEconomyPartyType; contact_person: string | null; email: string | null; phone: string | null; address: string | null; is_active: number }>
): Promise<void> {
  await api.put(`${base}/economy/parties/${id}`, body);
}

export async function getCostCenters(organizationId?: string): Promise<CostCenterOption[]> {
  const params = organizationId ? `?organizationId=${encodeURIComponent(organizationId)}` : '';
  const res = await api.get<{ success: boolean; data: CostCenterOption[] }>(
    `${base}/economy/cost-centers${params}`
  );
  return res.data.success ? res.data.data : [];
}

/** Download bokföring CSV for period (YYYY-MM). Uses authenticated request and triggers file save. */
export async function downloadExportBokforing(
  period: string,
  organizationId?: string
): Promise<void> {
  const params = new URLSearchParams({ period });
  if (organizationId) params.set('organizationId', organizationId);
  const res = await api.get<Blob>(`${base}/economy/export?${params.toString()}`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `unit4-bokforing-${period}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Download fakturering CSV for period (YYYY-MM). */
export async function downloadExportFakturering(
  period: string,
  organizationId?: string
): Promise<void> {
  const params = new URLSearchParams({ period });
  if (organizationId) params.set('organizationId', organizationId);
  const res = await api.get<Blob>(`${base}/economy/export-invoice?${params.toString()}`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `unit4-fakturering-${period}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function importEconomyRows(rows: Array<Record<string, unknown>>): Promise<{ applied: number; total: number }> {
  const res = await api.post<{ success: boolean; data: { applied: number; total: number } }>(
    `${base}/economy/import`,
    { rows }
  );
  if (!res.data.success) throw new Error('Import failed');
  return res.data.data;
}
